import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCreditCard } from '@fortawesome/pro-solid-svg-icons/faCreditCard';
import { faGauge } from '@fortawesome/pro-solid-svg-icons/faGauge';
import { faShieldHalved } from '@fortawesome/pro-solid-svg-icons/faShieldHalved';
import { faFileImport } from '@fortawesome/pro-solid-svg-icons/faFileImport';
import { faFileExport } from '@fortawesome/pro-solid-svg-icons/faFileExport';
import { faTag } from '@fortawesome/pro-solid-svg-icons/faTag';
import { faBoxes } from '@fortawesome/pro-solid-svg-icons/faBoxes';
import { faTruck } from '@fortawesome/pro-solid-svg-icons/faTruck';
import { faStore } from '@fortawesome/pro-solid-svg-icons/faStore';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';

import type { MessageDescriptor } from 'react-intl';

import { NotificationType, NotificationCategory } from './notification-hub.types';
import { messages } from './messages';

type DisplayCategoryConfig = {
  message: MessageDescriptor;
  icon: IconDefinition;
};

const BILLING_AND_SUBSCRIPTION: DisplayCategoryConfig = { message: messages.billingAndSubscription, icon: faCreditCard };
const USAGE_AND_LIMITS: DisplayCategoryConfig = { message: messages.usageAndLimits, icon: faGauge };
const SECURITY: DisplayCategoryConfig = { message: messages.security, icon: faShieldHalved };
const IMPORTS: DisplayCategoryConfig = { message: messages.imports, icon: faFileImport };
const EXPORTS: DisplayCategoryConfig = { message: messages.exports, icon: faFileExport };
const LABEL_CREATION: DisplayCategoryConfig = { message: messages.labelCreation, icon: faTag };
const INVENTORY: DisplayCategoryConfig = { message: messages.inventory, icon: faBoxes };
const CARRIER_SERVICES: DisplayCategoryConfig = { message: messages.carrierServices, icon: faTruck };
const STORE_CONNECTIONS: DisplayCategoryConfig = { message: messages.storeConnections, icon: faStore };
const SETTINGS: DisplayCategoryConfig = { message: messages.settings, icon: faGear };

const NOTIFICATION_TYPE_CONFIG: Partial<Record<NotificationType, DisplayCategoryConfig>> = {
  [NotificationType.PAYMENT_FAILED]: BILLING_AND_SUBSCRIPTION,
  [NotificationType.TRIAL_EXPIRED]: BILLING_AND_SUBSCRIPTION,
  [NotificationType.TRIAL_ENDING_SOON]: BILLING_AND_SUBSCRIPTION,
  [NotificationType.PENDING_CANCELLATION]: BILLING_AND_SUBSCRIPTION,
  [NotificationType.TAX_ID_MISSING]: BILLING_AND_SUBSCRIPTION,

  [NotificationType.SPENDING_LIMIT_WARNING]: USAGE_AND_LIMITS,
  [NotificationType.SHIPMENT_LIMIT_APPROACHING]: USAGE_AND_LIMITS,
  [NotificationType.INSURANCE_COVERAGE_WARNING]: USAGE_AND_LIMITS,

  [NotificationType.EMAIL_VERIFICATION_REQUIRED]: SECURITY,
  [NotificationType.PHONE_VERIFICATION_REQUIRED]: SECURITY,
  [NotificationType.API_KEYS_EXPIRING]: SECURITY,

  [NotificationType.ORDERS_IMPORTED]: IMPORTS,
  [NotificationType.STORE_IMPORT_FAILED]: IMPORTS,
  [NotificationType.STORE_IMPORT_PARTIAL]: IMPORTS,
  [NotificationType.CSV_IMPORT_COMPLETED]: IMPORTS,
  [NotificationType.CSV_IMPORT_COMPLETED_WITH_WARNINGS]: IMPORTS,
  [NotificationType.CSV_IMPORT_FAILED]: IMPORTS,
  [NotificationType.CUSTOMERS_IMPORTED]: IMPORTS,
  [NotificationType.CUSTOMERS_IMPORT_FAILED]: IMPORTS,
  [NotificationType.PRODUCT_IMPORT_COMPLETED]: IMPORTS,
  [NotificationType.PRODUCT_ALIAS_IMPORT_COMPLETED]: IMPORTS,
  [NotificationType.PRODUCT_BUNDLES_IMPORT_COMPLETED]: IMPORTS,
  [NotificationType.PRODUCT_IMPORT_FAILED]: IMPORTS,
  [NotificationType.INVENTORY_IMPORT_COMPLETED]: IMPORTS,
  [NotificationType.INVENTORY_IMPORT_FAILED]: IMPORTS,

  [NotificationType.EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.ORDERS_EXPORT_STARTED]: EXPORTS,
  [NotificationType.ORDERS_EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.ORDERS_EXPORT_FAILED]: EXPORTS,
  [NotificationType.SHIPMENTS_EXPORT_STARTED]: EXPORTS,
  [NotificationType.SHIPMENTS_EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.SHIPMENTS_EXPORT_FAILED]: EXPORTS,
  [NotificationType.PRODUCT_EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.PRODUCT_EXPORT_FAILED]: EXPORTS,
  [NotificationType.INVENTORY_EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.INVENTORY_EXPORT_FAILED]: EXPORTS,
  [NotificationType.LOCATIONS_EXPORT_STARTED]: EXPORTS,
  [NotificationType.LOCATIONS_EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.LOCATIONS_EXPORT_FAILED]: EXPORTS,
  [NotificationType.TOTES_EXPORT_STARTED]: EXPORTS,
  [NotificationType.TOTES_EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.TOTES_EXPORT_FAILED]: EXPORTS,
  [NotificationType.FORECASTING_EXPORT_FAILED]: EXPORTS,
  [NotificationType.FULFILLMENTS_EXPORT_STARTED]: EXPORTS,
  [NotificationType.FULFILLMENTS_EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.FULFILLMENTS_EXPORT_FAILED]: EXPORTS,
  [NotificationType.TRANSACTION_HISTORY_EXPORT_STARTED]: EXPORTS,
  [NotificationType.TRANSACTION_HISTORY_EXPORT_COMPLETED]: EXPORTS,
  [NotificationType.TRANSACTION_HISTORY_EXPORT_FAILED]: EXPORTS,
  [NotificationType.RETURNS_EXPORT_COMPLETED]: EXPORTS,

  [NotificationType.LABELS_CREATED]: LABEL_CREATION,
  [NotificationType.LABELS_PRINTED]: LABEL_CREATION,
  [NotificationType.BATCH_COMPLETED]: LABEL_CREATION,
  [NotificationType.BATCH_FAILED]: LABEL_CREATION,

  [NotificationType.INVENTORY_SYNCED]: INVENTORY,

  [NotificationType.USPS_NSA_ONBOARDING]: CARRIER_SERVICES,
  [NotificationType.USPS_NSA_WAITING]: CARRIER_SERVICES,

  [NotificationType.STORE_CONNECTED]: STORE_CONNECTIONS,
  [NotificationType.STORE_CONNECTION_LOST]: STORE_CONNECTIONS,

  [NotificationType.SETTINGS_SAVED]: SETTINGS,
};

const CATEGORY_FALLBACK: Partial<Record<NotificationCategory, DisplayCategoryConfig>> = {
  [NotificationCategory.BILLING]: { message: messages.billing, icon: faCreditCard },
  [NotificationCategory.ORDERS]: { message: messages.orders, icon: faTag },
  [NotificationCategory.SHIPPING]: { message: messages.shipping, icon: faTruck },
  [NotificationCategory.INVENTORY]: INVENTORY,
  [NotificationCategory.SYSTEM]: { message: messages.system, icon: faGear },
};

const DEFAULT_CONFIG: DisplayCategoryConfig = { message: messages.system, icon: faGear };

export const getDisplayCategoryConfig = (notificationType: string, category: string): DisplayCategoryConfig => {
  return (
    NOTIFICATION_TYPE_CONFIG[notificationType as NotificationType] ?? CATEGORY_FALLBACK[category as NotificationCategory] ?? DEFAULT_CONFIG
  );
};
